/**
 * Help Content for G.S Workflow Application
 * This content is used by both the frontend help system and Sophie AI assistant
 */

export const HELP_CONTENT = {
  bulk_email: {
    title: "Bulk Email Guide",
    description: "Send professional emails to multiple organizations at once",
    sections: [
      {
        title: "Getting Started with Bulk Email",
        icon: "Mail",
        steps: [
          {
            step: 1,
            title: "Navigate to Email Hub",
            description: "Click 'Email Hub' in the left navigation menu, then select the 'Bulk Email' tab at the top of the page."
          },
          {
            step: 2,
            title: "Create a New Campaign",
            description: "Click the '+ Create Campaign' button to start a new bulk email campaign. Give your campaign a descriptive name (e.g., 'Term 2 2026 Photography Reminder')."
          },
          {
            step: 3,
            title: "Select Recipients",
            description: "Use the sector filters to narrow down your recipient list. You can select multiple sectors by clicking on the sector chips (e.g., Schools, Preschools). Use the search box to find specific organizations by name."
          },
          {
            step: 4,
            title: "Check Selected Recipients",
            description: "Selected organizations will be highlighted. Review the count showing 'X recipients selected'. Click on any row to toggle selection. Use 'Select All' to select all visible organizations."
          }
        ]
      },
      {
        title: "Composing Your Email",
        icon: "Edit",
        steps: [
          {
            step: 1,
            title: "Write Your Subject Line",
            description: "Enter a clear, descriptive subject. You can use merge fields like {{org_name}} which will be replaced with each organization's name."
          },
          {
            step: 2,
            title: "Compose the Email Body",
            description: "Write your email content in the body field. Available merge fields:\n• {{org_name}} - Organization name\n• {{contact_name}} - Primary contact's name\n• {{email}} - Contact's email address"
          },
          {
            step: 3,
            title: "Add Attachments (Optional)",
            description: "Click 'Add Attachment' to attach files like brochures, price lists, or information sheets. Multiple attachments can be added. Click the eye icon to preview attachments before sending."
          },
          {
            step: 4,
            title: "Use AI Polish (Optional)",
            description: "Click the sparkle icon to have Sophie AI improve your email's tone and professionalism while keeping your message intact."
          }
        ]
      },
      {
        title: "Scheduling & Sending",
        icon: "Calendar",
        steps: [
          {
            step: 1,
            title: "Choose Send Time",
            description: "Select 'Send Now' for immediate delivery, or choose 'Schedule' to pick a specific date and time. Scheduled emails can be edited or cancelled before the send time."
          },
          {
            step: 2,
            title: "Preview Before Sending",
            description: "Click 'Preview' to see exactly how your email will look for a sample recipient. Check that merge fields are working correctly."
          },
          {
            step: 3,
            title: "Send or Schedule",
            description: "Click 'Send Campaign' or 'Schedule Campaign' to finalize. A confirmation dialog will show the total recipient count."
          },
          {
            step: 4,
            title: "Track Your Campaign",
            description: "After sending, view the campaign status in the 'Sent Emails' tab. You can see delivery status and any bounced emails."
          }
        ]
      },
      {
        title: "Using Email Templates",
        icon: "FileText",
        steps: [
          {
            step: 1,
            title: "Create a Template",
            description: "In the 'Templates' tab, click '+ New Template'. Give it a name, category, and fill in the subject and body."
          },
          {
            step: 2,
            title: "Apply a Template",
            description: "When composing an email, click 'Use Template' to load a saved template. This pre-fills the subject and body fields."
          },
          {
            step: 3,
            title: "Edit Templates",
            description: "Click the edit icon on any template card to modify it. Changes are saved for future use."
          }
        ]
      },
      {
        title: "Tips & Best Practices",
        icon: "Lightbulb",
        tips: [
          "Always preview your email before sending to check merge fields work correctly",
          "Use descriptive campaign names to easily find them later",
          "Schedule emails for business hours (9am-5pm) for better open rates",
          "Keep subject lines under 50 characters for mobile readability",
          "Test with a single recipient first for important campaigns",
          "Attachments are stored securely and won't be lost after system updates"
        ]
      }
    ]
  },

  workflows: {
    title: "Workflows & Milestones Guide",
    description: "Track organization progress through your photography workflow",
    sections: [
      {
        title: "Understanding Workflows",
        icon: "GitBranch",
        steps: [
          {
            step: 1,
            title: "What is a Workflow?",
            description: "A workflow represents the complete journey of an organization from initial contact to final delivery. Each workflow contains milestones that track progress through key stages."
          },
          {
            step: 2,
            title: "Workflow Status",
            description: "Workflows have statuses:\n• Active - Currently in progress\n• Pending - Waiting to start\n• Completed - All milestones done\n• Cancelled - Workflow stopped"
          },
          {
            step: 3,
            title: "Viewing Workflows",
            description: "Navigate to 'Workflows' in the left menu. Use filters to view by status, sector, or search by organization name. Click any workflow card to view details."
          }
        ]
      },
      {
        title: "Creating a New Workflow",
        icon: "Plus",
        steps: [
          {
            step: 1,
            title: "Start a Workflow",
            description: "From the Workflows page, click '+ New Workflow'. Select the organization from the dropdown list."
          },
          {
            step: 2,
            title: "Choose Workflow Template",
            description: "Select a template that matches your process (e.g., 'School Photography', 'Sports Club', 'Preschool'). This pre-populates the milestones."
          },
          {
            step: 3,
            title: "Set Year",
            description: "Select the year for this workflow. This helps organize workflows by photography season."
          },
          {
            step: 4,
            title: "Add Notes (Optional)",
            description: "Add any special notes about this workflow. These are visible on the workflow card for quick reference."
          }
        ]
      },
      {
        title: "Managing Milestones",
        icon: "CheckCircle",
        steps: [
          {
            step: 1,
            title: "Understanding Milestones",
            description: "Milestones are key checkpoints in your workflow:\n• Booking Confirmed\n• Envelopes Sent\n• Photo Day Completed\n• Proofing Ready\n• Orders Processed\n• Delivery Complete"
          },
          {
            step: 2,
            title: "Completing a Milestone",
            description: "Click on a milestone to mark it complete. Enter the completion date and any notes. The workflow progress bar will update automatically."
          },
          {
            step: 3,
            title: "Milestone Timeline",
            description: "View the timeline to see completed milestones with dates. Hover over any milestone to see completion details and who marked it complete."
          },
          {
            step: 4,
            title: "Adding Custom Milestones",
            description: "Click '+ Add Milestone' to add a custom checkpoint. Enter the name and optional due date. Custom milestones appear in the timeline alongside standard ones."
          },
          {
            step: 5,
            title: "Editing Milestones",
            description: "Click the edit icon on any milestone to change its details. You can update the name, due date, or notes. Completed milestones can be reopened if needed."
          }
        ]
      },
      {
        title: "Workflow Templates",
        icon: "Layout",
        steps: [
          {
            step: 1,
            title: "Using Templates",
            description: "Workflow templates pre-define the milestones for different organization types. Admin users can create and modify templates in Settings."
          },
          {
            step: 2,
            title: "Template Types",
            description: "Common templates include:\n• School Photography (6-8 milestones)\n• Sports Club (4-6 milestones)\n• Preschool (5-7 milestones)\n• Custom (define your own)"
          },
          {
            step: 3,
            title: "Creating a Template (Admin)",
            description: "Go to Settings > Workflows tab. Click '+ New Template', name it, and add the milestone steps in order. Save to make it available for all users."
          }
        ]
      },
      {
        title: "Tips & Best Practices",
        icon: "Lightbulb",
        tips: [
          "Complete milestones promptly to keep the dashboard accurate",
          "Use the notes field to record important details or issues",
          "Filter workflows by 'Active' status to focus on current work",
          "Check the 'Due Soon' section on the dashboard for upcoming deadlines",
          "Use workflow search to quickly find a specific organization",
          "Review completed workflows to ensure nothing was missed before archiving"
        ]
      }
    ]
  },

  document_templates: {
    title: "Document Templates Guide",
    description: "Create and use document templates for confirmations, job sheets, and more",
    sections: [
      {
        title: "Understanding Document Templates",
        icon: "FileText",
        steps: [
          {
            step: 1,
            title: "What are Document Templates?",
            description: "Document templates are .docx files with placeholder fields that get filled with organization data. Use them for confirmation letters, job sheets, production sheets, and more."
          },
          {
            step: 2,
            title: "Default vs Custom Templates",
            description: "The system includes default templates for common documents. You can also upload your own custom templates with specific formatting and fields."
          },
          {
            step: 3,
            title: "Where to Find Templates",
            description: "Navigate to 'Resource Library' in the left menu. Click the 'Document Templates' tab to view, upload, and manage templates."
          }
        ]
      },
      {
        title: "Using Placeholder Fields",
        icon: "Code",
        steps: [
          {
            step: 1,
            title: "How Placeholders Work",
            description: "Placeholders in templates look like: {{Field Name}}. When generating a document, these are replaced with actual data from the organization."
          },
          {
            step: 2,
            title: "Organization Fields",
            description: "Available organization placeholders:\n• {{Company}} - Organization name\n• {{Address Line 1}} - Street address\n• {{City / Town, State / County / Province Zip / Postal Code}} - Full address line\n• {{First Name Last Name}} - Contact full name\n• {{First Name}} - Contact first name only\n• {{Phone 1}} - Primary phone\n• {{Email Address}} - Contact email"
          },
          {
            step: 3,
            title: "Year-Based Fields",
            description: "Sales data placeholders include the year:\n• {{Year 2026/Sales/Photography Date 1}} - First photo date\n• {{Year 2026/Sales/Photography Date 2}} - Second photo date\n• {{Year 2026/Sales/Photography Start Time}} - Start time\n• {{Year 2026/Sales/Number of Enrolled Students}} - Student count\n• {{Year 2026/Sales/Package Selected}} - Selected package"
          },
          {
            step: 4,
            title: "Viewing Available Placeholders",
            description: "In the Resource Library, click 'Show Placeholders' button to see all available fields organized by category. You can copy these directly into your templates."
          }
        ]
      },
      {
        title: "Uploading Templates",
        icon: "Upload",
        steps: [
          {
            step: 1,
            title: "Prepare Your Template",
            description: "Create your document in Microsoft Word (.docx format). Add placeholder fields where you want data inserted. Use the exact placeholder format shown in the placeholders panel."
          },
          {
            step: 2,
            title: "Upload Single Template",
            description: "In Document Templates tab, click 'Upload Template'. Select your .docx file, enter a name and description, then click Upload."
          },
          {
            step: 3,
            title: "Bulk Upload",
            description: "Click 'Bulk Upload' to upload multiple templates at once. The template names will be derived from the filenames."
          },
          {
            step: 4,
            title: "Verify Upload",
            description: "After upload, your template appears in the list. Click the eye icon to preview it, or click 'Edit' to open in Google Docs for modifications."
          }
        ]
      },
      {
        title: "Editing Templates with Google Docs",
        icon: "Edit",
        steps: [
          {
            step: 1,
            title: "Open for Editing",
            description: "Click the 'Edit' button on any template card. This opens the template in Google Docs for professional editing with full formatting tools."
          },
          {
            step: 2,
            title: "Make Your Changes",
            description: "In Google Docs, edit the text, formatting, add images, tables, or adjust placeholders. The document saves automatically to your Google Drive."
          },
          {
            step: 3,
            title: "Sync Changes Back",
            description: "After editing, return to G.S Workflow and click 'Sync from Google Docs' to save your changes back to the system template."
          },
          {
            step: 4,
            title: "Editing Default Templates",
            description: "When you edit a default template, a custom copy is created. This preserves the original default while letting you customize as needed."
          }
        ]
      },
      {
        title: "Generating Documents",
        icon: "FileOutput",
        steps: [
          {
            step: 1,
            title: "Navigate to Organization",
            description: "Go to the Organizations page and find the organization you need a document for. Click to open their detail page."
          },
          {
            step: 2,
            title: "Generate Confirmation Letter",
            description: "Click the 'Generate Confirmation Letter' button. Select the template type and year. Click 'Generate' to create the document."
          },
          {
            step: 3,
            title: "Download or View",
            description: "The generated document opens in a new tab or downloads automatically. All placeholders are replaced with the organization's actual data."
          },
          {
            step: 4,
            title: "Regenerate if Needed",
            description: "If data changes, you can regenerate the document at any time. Each generation uses the latest organization information."
          }
        ]
      },
      {
        title: "Managing Templates",
        icon: "Settings",
        steps: [
          {
            step: 1,
            title: "Rename Templates",
            description: "Click the edit (pencil) icon on any template card to change its name or description. Press Enter or click the checkmark to save."
          },
          {
            step: 2,
            title: "Delete Templates",
            description: "Click the trash icon to delete a template. Custom templates are permanently removed. Default templates can be restored by an admin."
          },
          {
            step: 3,
            title: "Bulk Delete",
            description: "Use the checkboxes to select multiple templates, then click 'Delete Selected' to remove them all at once."
          }
        ]
      },
      {
        title: "Tips & Best Practices",
        icon: "Lightbulb",
        tips: [
          "Test templates with a sample organization before using for real communications",
          "Use consistent placeholder formatting - copy from the Placeholders panel",
          "Keep templates simple - complex formatting may not merge correctly",
          "Back up custom templates by downloading them before making changes",
          "Use descriptive template names to easily find them later",
          "Check generated documents before sending to clients",
          "Templates are stored securely in the cloud and won't be lost during updates"
        ]
      }
    ]
  }
};

/**
 * Get help content for Sophie AI assistant
 * Returns formatted text that Sophie can use to help users
 */
export const getSophieHelpContext = () => {
  let context = "# G.S Workflow Help Documentation\n\n";
  context += "Use this information to help users with questions about the system.\n\n";
  
  Object.entries(HELP_CONTENT).forEach(([key, content]) => {
    context += `## ${content.title}\n`;
    context += `${content.description}\n\n`;
    
    content.sections.forEach(section => {
      context += `### ${section.title}\n`;
      
      if (section.steps) {
        section.steps.forEach(step => {
          context += `${step.step}. **${step.title}**: ${step.description}\n`;
        });
      }
      
      if (section.tips) {
        context += "**Tips:**\n";
        section.tips.forEach(tip => {
          context += `• ${tip}\n`;
        });
      }
      
      context += "\n";
    });
    
    context += "\n---\n\n";
  });
  
  return context;
};

export default HELP_CONTENT;
